/**
 * The edits a layer makes to a preset: hide a module, resize a box, move a module into another split,
 * reorder a split, change the scene or move the stage down. A domain, a screen or an accessibility mode is a list of these —
 * data, so "the seat map wants a taller content area" is one line in the trains pack.
 *
 * Every change is applied to a copy and reported: done, or why not (an unknown target is a note, not a crash).
 */
import { z } from 'zod';
import { sizeSchema, type LayoutNode, type Preset, type Size, type SplitNode } from './model';

export const layoutChangeSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('hide'), module: z.string() }).strict(),
  z.object({ op: z.literal('resize'), target: z.string(), size: sizeSchema }).strict(),
  z.object({
    op: z.literal('move'),
    module: z.string(),
    into: z.string(),
    /** Where in `into`: before or after a sibling (by name), at an index, or last. */
    before: z.string().optional(),
    after: z.string().optional(),
    index: z.number().int().optional(),
  }).strict(),
  z.object({ op: z.literal('order'), split: z.string(), children: z.array(z.string()) }).strict(),
  /** Place a module that is not in the layout (the editor's "add", or a pack adding one of its own). */
  z.object({ op: z.literal('add'), module: z.string(), into: z.string(), index: z.number().int().optional(), size: sizeSchema.optional() }).strict(),
  z.object({ op: z.literal('scene'), scene: z.string() }).strict(),
  /** Move the stage down (added to Preset.stageOffsetY): its avatar's lower edge under what is below it. */
  z.object({ op: z.literal('stageOffset'), y: z.number().min(0) }).strict(),
]);
export type LayoutChange = z.infer<typeof layoutChangeSchema>;

export interface ChangeOutcome {
  applied: boolean;
  note?: string;
}

/** A node's name for changes: a slot is named by its module, a split by its id. */
export function nameOf(node: LayoutNode): string {
  return node.kind === 'slot' ? node.module : node.id;
}

export function applyChange(preset: Preset, change: LayoutChange): { preset: Preset; outcome: ChangeOutcome } {
  const next = structuredClone(preset);
  switch (change.op) {
    case 'scene':
      next.scene = change.scene;
      return done(next);
    case 'stageOffset':
      next.stageOffsetY = (next.stageOffsetY ?? 0) + change.y;
      return done(next);
    case 'hide': {
      const removed = removeNode(next.root, change.module);
      if (!removed) return notDone(preset, `module "${change.module}" is not in the layout`);
      pruneEmpty(next.root);
      return done(next);
    }
    case 'resize': {
      const node = findNode(next.root, change.target);
      if (!node) return notDone(preset, `"${change.target}" is not in the layout`);
      node.size = change.size as Size;
      return done(next);
    }
    case 'move': {
      const target = findNode(next.root, change.into);
      if (!target || target.kind !== 'split') return notDone(preset, `split "${change.into}" is not in the layout`);
      const node = removeNode(next.root, change.module);
      if (!node) return notDone(preset, `module "${change.module}" is not in the layout`);
      const at = insertionIndex(target, change);
      target.children.splice(at, 0, node);
      pruneEmpty(next.root);
      return done(next);
    }
    case 'add': {
      if (findNode(next.root, change.module)) return notDone(preset, `module "${change.module}" is already in the layout`);
      const target = findNode(next.root, change.into);
      if (!target || target.kind !== 'split') return notDone(preset, `split "${change.into}" is not in the layout`);
      const node: LayoutNode = change.size ? { kind: 'slot', module: change.module, size: change.size as Size } : { kind: 'slot', module: change.module };
      target.children.splice(insertionIndex(target, change), 0, node);
      return done(next);
    }
    case 'order': {
      const split = findNode(next.root, change.split);
      if (!split || split.kind !== 'split') return notDone(preset, `split "${change.split}" is not in the layout`);
      const rank = (n: LayoutNode) => {
        const i = change.children.indexOf(nameOf(n));
        return i === -1 ? change.children.length : i;
      };
      split.children = [...split.children].sort((a, b) => rank(a) - rank(b));
      return done(next);
    }
  }
}

function done(preset: Preset) {
  return { preset, outcome: { applied: true } };
}

function notDone(preset: Preset, note: string) {
  return { preset, outcome: { applied: false, note } };
}

function insertionIndex(target: SplitNode, change: { before?: string; after?: string; index?: number }): number {
  const sibling = change.before ?? change.after;
  if (sibling !== undefined) {
    const i = target.children.findIndex((c) => nameOf(c) === sibling);
    if (i !== -1) return change.before !== undefined ? i : i + 1;
  }
  return change.index === undefined ? target.children.length : clamp(change.index, 0, target.children.length);
}

function clamp(n: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, n));
}

export function findNode(root: SplitNode, name: string): LayoutNode | null {
  if (root.id === name) return root;
  for (const child of root.children) {
    if (nameOf(child) === name) return child;
    if (child.kind === 'split') {
      const found = findNode(child, name);
      if (found) return found;
    }
  }
  return null;
}

function removeNode(root: SplitNode, name: string): LayoutNode | null {
  const i = root.children.findIndex((c) => nameOf(c) === name);
  if (i !== -1) return root.children.splice(i, 1)[0] ?? null;
  for (const child of root.children) {
    if (child.kind === 'split') {
      const found = removeNode(child, name);
      if (found) return found;
    }
  }
  return null;
}

/** A split left without children takes no space: drop it. */
function pruneEmpty(split: SplitNode): void {
  split.children = split.children.filter((c) => {
    if (c.kind === 'slot') return true;
    pruneEmpty(c);
    return c.children.length > 0;
  });
}

/** The split holding a node, and where. */
export function parentOf(root: SplitNode, name: string): { parent: SplitNode; index: number } | null {
  const i = root.children.findIndex((c) => nameOf(c) === name);
  if (i !== -1) return { parent: root, index: i };
  for (const child of root.children) {
    if (child.kind === 'split') {
      const found = parentOf(child, name);
      if (found) return found;
    }
  }
  return null;
}
